// Firebase Analytics utility wrapper
// Provides consistent interface for tracking events and setting user properties

import {
  getAnalytics as getAnalyticsSDK,
  isSupported as isAnalyticsSupported,
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from 'firebase/analytics'
import { app } from '@/firebase'
import createLogger from '@/utils/logger'
import type {
  AnalyticsUserProperties,
  BeginCheckoutEventParams,
  ClassEnrollEventParams,
  ClassViewEventParams,
  CreatedAccountEventParams,
  LoginEventParams,
  PurchasedConsultationEventParams,
  SignUpEventParams,
  LiveClassJoinEventParams,
  LiveClassRegistrationEventParams,
  LiveClassAudioAssistPromptedEventParams,
  LiveClassAudioAssistOutcomeEventParams,
  PageViewEventParams,
  PurchaseEventParams,
  RecordingCompleteEventParams,
  RecordingPlayEventParams,
  RecordingProgressEventParams,
  SelectContentEventParams,
  PWAInstallPromptShownEventParams,
  PWAInstallPromptResponseEventParams,
  PWAInstalledEventParams,
  PWALaunchedEventParams,
  ExternalCalendarEventParams,
} from '@gh/common/models'

const logger = createLogger('utils/analytics.ts')

// Initialize Firebase Analytics (required for Remote Config user property targeting)
// Analytics must be initialized before Remote Config to enable user property conditions
export const analyticsPromise = isAnalyticsSupported()
  .then((supported) => {
    if (!supported) {
      logger.warn('Firebase Analytics is not supported in this environment')
      return null
    }
    const instance = getAnalyticsSDK(app)
    logger.log('Firebase Analytics initialized')
    return instance
  })
  .catch((error) => {
    logger.warn(
      'Firebase Analytics initialization failed. User property targeting in Remote Config will not be available.',
      error,
    )
    return null
  })

let analyticsInstance: Analytics | null = null
let isDebugModeEnabled = false

/**
 * Get the Analytics instance (lazy loaded)
 * @returns Analytics instance or null if not available
 */
async function getAnalytics(): Promise<Analytics | null> {
  if (analyticsInstance) return analyticsInstance
  analyticsInstance = await analyticsPromise
  if (!analyticsInstance) {
    logger.debug('Analytics not available')
  }
  return analyticsInstance
}

/**
 * Internal helper to track an event
 */
async function trackEvent(eventName: string, params?: object): Promise<void> {
  const instance = await getAnalytics()
  if (!instance) {
    logger.debug('Analytics not available - event not tracked:', eventName)
    return
  }

  try {
    const finalParams = isDebugModeEnabled ? { ...params, debug_mode: true } : params
    logEvent(instance, eventName, finalParams)
    logger.log('Tracked event:', eventName)
  } catch (error) {
    logger.error('Failed to track event:', error)
  }
}

// ============================================================================
// Event Definitions
// ============================================================================

/**
 * Type for analytics event definition entries.
 */
type EventDefinition =
  | { eventName: string; functionName: string; paramsType: unknown }
  | { eventName: string; functionName: string }

/**
 * Helper to define analytics event definition arrays with type safety and const assertion.
 */
function defineAnalyticsEvents<T extends readonly EventDefinition[]>(definitions: T): T {
  return definitions
}

/**
 * Derives a { [functionName]: Function } object type from event definitions array.
 * If paramsType is provided, the function requires params of that type.
 * If paramsType is not provided, the function takes no arguments.
 */
type AnalyticsEventsToObject<T extends readonly EventDefinition[]> = {
  [K in T[number]['functionName']]: Extract<T[number], { functionName: K }> extends {
    paramsType: infer P
  }
    ? (params: P) => Promise<void>
    : () => Promise<void>
}

/**
 * Define all analytics events in one place.
 * When adding a new event:
 * 1. Import the event parameter type from @gh/common/models
 * 2. Add { eventName, functionName, paramsType } to the array below.
 *    - If paramsType is provided, the function will require params of that type.
 *    - If paramsType is omitted, the function will take no arguments.
 */
const eventDefinitions = defineAnalyticsEvents([
  // Authentication Events
  { eventName: 'login', functionName: 'trackLogin', paramsType: {} as LoginEventParams },
  { eventName: 'logout', functionName: 'trackLogout' },
  { eventName: 'sign_up', functionName: 'trackSignUp', paramsType: {} as SignUpEventParams },
  // Signup Funnel Events
  { eventName: 'get_started', functionName: 'trackGetStarted' },
  { eventName: 'started_signup', functionName: 'trackStartedSignup' },
  {
    eventName: 'created_account',
    functionName: 'trackCreatedAccount',
    paramsType: {} as CreatedAccountEventParams,
  },
  { eventName: 'completed_profile', functionName: 'trackCompletedProfile' },
  { eventName: 'activated_free_trial', functionName: 'trackActivatedFreeTrial' },
  {
    eventName: 'purchased_subscription',
    functionName: 'trackPurchasedSubscription',
    paramsType: {} as PurchaseEventParams,
  },
  {
    eventName: 'purchased_consultation',
    functionName: 'trackPurchasedConsultation',
    paramsType: {} as PurchasedConsultationEventParams,
  },
  // Page View Events
  { eventName: 'page_view', functionName: 'trackPageView', paramsType: {} as PageViewEventParams },
  // Engagement Events
  {
    eventName: 'select_content',
    functionName: 'trackSelectContent',
    paramsType: {} as SelectContentEventParams,
  },
  // Class Engagement Events
  {
    eventName: 'class_view',
    functionName: 'trackClassView',
    paramsType: {} as ClassViewEventParams,
  },
  {
    eventName: 'class_enroll',
    functionName: 'trackClassEnroll',
    paramsType: {} as ClassEnrollEventParams,
  },
  {
    eventName: 'class_unenroll',
    functionName: 'trackClassUnenroll',
    paramsType: {} as ClassEnrollEventParams,
  },
  {
    eventName: 'external_calendar_event',
    functionName: 'trackExternalCalendarEvent',
    paramsType: {} as ExternalCalendarEventParams,
  },
  // Live Class Events
  {
    eventName: 'live_class_join_click',
    functionName: 'trackLiveClassJoinClick',
    paramsType: {} as LiveClassJoinEventParams,
  },
  {
    eventName: 'live_class_joined',
    functionName: 'trackLiveClassJoined',
    paramsType: {} as LiveClassJoinEventParams,
  },
  {
    eventName: 'live_class_registration',
    functionName: 'trackLiveClassRegistration',
    paramsType: {} as LiveClassRegistrationEventParams,
  },
  {
    eventName: 'live_class_audio_assist_prompted',
    functionName: 'trackLiveClassAudioAssistPrompted',
    paramsType: {} as LiveClassAudioAssistPromptedEventParams,
  },
  {
    eventName: 'live_class_audio_assist_outcome',
    functionName: 'trackLiveClassAudioAssistOutcome',
    paramsType: {} as LiveClassAudioAssistOutcomeEventParams,
  },
  // Recording Events
  {
    eventName: 'recording_play',
    functionName: 'trackRecordingPlay',
    paramsType: {} as RecordingPlayEventParams,
  },
  {
    eventName: 'recording_progress',
    functionName: 'trackRecordingProgress',
    paramsType: {} as RecordingProgressEventParams,
  },
  {
    eventName: 'recording_complete',
    functionName: 'trackRecordingComplete',
    paramsType: {} as RecordingCompleteEventParams,
  },
  // Conversion Events
  {
    eventName: 'begin_checkout',
    functionName: 'trackBeginCheckout',
    paramsType: {} as BeginCheckoutEventParams,
  },
  { eventName: 'purchase', functionName: 'trackPurchase', paramsType: {} as PurchaseEventParams },
  // PWA Events
  {
    eventName: 'pwa_install_prompt_shown',
    functionName: 'trackPWAInstallPromptShown',
    paramsType: {} as PWAInstallPromptShownEventParams,
  },
  {
    eventName: 'pwa_install_prompt_response',
    functionName: 'trackPWAInstallPromptResponse',
    paramsType: {} as PWAInstallPromptResponseEventParams,
  },
  {
    eventName: 'pwa_installed',
    functionName: 'trackPWAInstalled',
    paramsType: {} as PWAInstalledEventParams,
  },
  {
    eventName: 'pwa_launched',
    functionName: 'trackPWALaunched',
    paramsType: {} as PWALaunchedEventParams,
  },
] as const)

// ============================================================================
// Create Analytics Tracking Object
// ============================================================================

/**
 * Creates an object with all tracking functions, similar to repository pattern.
 * Each function is generated from the event definitions.
 */
function createAnalyticsTracker<T extends readonly EventDefinition[]>(
  definitions: T,
): AnalyticsEventsToObject<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tracker = {} as Record<string, (...args: any[]) => Promise<void>>

  for (const def of definitions) {
    if ('paramsType' in def) {
      // Params required
      tracker[def.functionName] = async (params: object): Promise<void> => {
        await trackEvent(def.eventName, params)
      }
    } else {
      // No params
      tracker[def.functionName] = async (): Promise<void> => {
        await trackEvent(def.eventName)
      }
    }
  }

  return tracker as AnalyticsEventsToObject<T>
}

// Create and export the events object
// Usage: events.trackLogin({ method: 'google' })
export const events = createAnalyticsTracker(eventDefinitions)

/**
 * Export event definitions for use in stores that need to wrap events
 * Maps function names to their corresponding event names
 */
export const eventNameMap = Object.fromEntries(
  eventDefinitions.map((def) => [def.functionName, def.eventName]),
) as Record<string, string>

// Export the main analytics object containing events
export const analytics = {
  events,
  setUserId: setAnalyticsUserId,
  setUserProperties: setAnalyticsUserProperties,
  setDebugMode: setAnalyticsDebugMode,
  isAvailable: isAnalyticsAvailable,
  trackCustomEvent,
}

// ============================================================================
// Generic Event Tracker (fallback for custom events)
// ============================================================================

/**
 * Enable or disable debug mode for Analytics.
 * - Sets our internal flag so event params include debug_mode (for consistency).
 * - Calls gtag('config', ..., { debug_mode }) so GA4 Debug View actually shows events.
 *   (Debug View only works when debug_mode is sent in the tag config, not just event params.)
 * Alternative for web: install the "Google Analytics Debugger" Chrome extension and enable it.
 */
async function setAnalyticsDebugMode(enabled: boolean): Promise<void> {
  isDebugModeEnabled = enabled
  logger.log('Analytics debug mode:', enabled ? 'ENABLED' : 'DISABLED')

  await getAnalytics()
  const measurementId = app.options.measurementId
  const gtag = (
    globalThis as typeof globalThis & { gtag?: (cmd: string, id: string, config?: object) => void }
  ).gtag
  if (measurementId && typeof gtag === 'function') {
    try {
      gtag('config', measurementId, { debug_mode: enabled })
      logger.log('gtag config debug_mode set:', enabled)
    } catch (err) {
      logger.warn('Failed to set gtag debug_mode (Debug View may not show events):', err)
    }
  } else if (enabled) {
    logger.warn(
      'GA4 Debug View may not show events: gtag or measurementId not available. ' +
        'Try the "Google Analytics Debugger" Chrome extension, or ensure Analytics is initialized.',
    )
  }
}

/**
 * Track a custom event with arbitrary parameters
 * Use typed methods above for standard events
 * @param eventName - The name of the event to track
 * @param params - Optional parameters to attach to the event
 */
async function trackCustomEvent(
  eventName: string,
  params?: Record<string, unknown>,
): Promise<void> {
  await trackEvent(eventName, params)
}

/**
 * Set the user ID for Analytics
 * @param userId - The user ID to set, or null to clear
 */
async function setAnalyticsUserId(userId: string | null): Promise<void> {
  const instance = await getAnalytics()
  if (!instance) {
    logger.debug('Analytics not available - user ID not set')
    return
  }

  try {
    setUserId(instance, userId)
    logger.log('Set user ID:', userId)
  } catch (error) {
    logger.error('Failed to set user ID:', error)
  }
}

/**
 * Set user properties for Analytics
 * @param properties - User properties to set (partial - only set the properties you want to update)
 */
async function setAnalyticsUserProperties(
  properties: Partial<AnalyticsUserProperties>,
): Promise<void> {
  const instance = await getAnalytics()
  if (!instance) {
    logger.debug('Analytics not available - user properties not set')
    return
  }

  try {
    setUserProperties(instance, properties)
    logger.log('Set user properties', {
      keys: Object.keys(properties),
    })
  } catch (error) {
    logger.error('Failed to set user properties:', error)
  }
}

/**
 * Check if Analytics is available
 * @returns True if Analytics is initialized and available
 */
async function isAnalyticsAvailable(): Promise<boolean> {
  const instance = await getAnalytics()
  return instance !== null
}
